import React, { useState, useEffect, useRef } from 'react';
import { ArrowRight } from 'lucide-react';

const ConfirmationSlider = ({ onConfirm, text = 'Desliza para confirmar' }) => {
  const [isDragging, setIsDragging] = useState(false);
  const [position, setPosition] = useState({ thumb: 0, progress: 0 });
  const sliderRef = useRef(null);
  const thumbRef = useRef(null);

  const updatePosition = (clientX) => {
    if (!sliderRef.current || !thumbRef.current) return;
    
    const sliderRect = sliderRef.current.getBoundingClientRect();
    const thumbWidth = thumbRef.current.offsetWidth;
    const availableWidth = sliderRect.width - thumbWidth;
    
    // Calcular la posición limitada al espacio disponible
    const offsetX = clientX - sliderRect.left - (thumbWidth / 2); // Centrar el thumb en el punto de toque
    const boundedOffset = Math.max(0, Math.min(availableWidth, offsetX));
    
    // Calcular posición del thumb y progreso
    const thumbPosition = boundedOffset;
    const progressPercentage = (boundedOffset / availableWidth) * 100;
    
    setPosition({ thumb: thumbPosition, progress: progressPercentage });
    
    // Confirmar cuando llegamos al final
    if (progressPercentage >= 98) {
      setPosition({ thumb: availableWidth, progress: 100 });
      setIsDragging(false);
      onConfirm();
    }
  };

  const handleStart = (e) => {
    if (!sliderRef.current || !thumbRef.current) return;
    e.preventDefault();
    e.stopPropagation();
    
    const clientX = e.type.includes('mouse') ? e.clientX : e.touches[0].clientX;
    setIsDragging(true);
    updatePosition(clientX);
  };

  const handleMove = (clientX) => {
    if (!isDragging) return;
    updatePosition(clientX);
  };

  const handleEnd = () => {
    if (isDragging) {
      setIsDragging(false);
      if (position.progress < 98) {
        setPosition({ thumb: 0, progress: 0 });
      }
    }
  };

  useEffect(() => {
    if (!isDragging) return;

    const handleTouchMove = (e) => {
      if (isDragging) {
        handleMove(e.touches[0].clientX);
      }
    };

    const handleMouseMove = (e) => {
      handleMove(e.clientX);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleEnd);
    document.addEventListener('touchmove', handleTouchMove);
    document.addEventListener('touchend', handleEnd);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleEnd);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleEnd);
    };
  }, [isDragging]);

  return (
    <div 
      ref={sliderRef}
      className="relative h-16 bg-gray-100 dark:bg-gray-700 rounded-xl overflow-hidden select-none touch-none"
    >
      {/* Barra de progreso */}
      <div 
        className="absolute inset-y-0 left-0 bg-green-500 dark:bg-green-600 transition-opacity"
        style={{ width: `${position.progress}%`, opacity: 0.2 }}
      />

      {/* Texto central */}
      <div className="absolute inset-0 flex items-center justify-center text-gray-600 dark:text-gray-300 font-medium text-lg pointer-events-none select-none">
        {text}
      </div>

      {/* Thumb */}
      <div
        ref={thumbRef}
        onMouseDown={handleStart}
        onTouchStart={handleStart}
        className="absolute top-0 bottom-0 left-0 w-24 cursor-grab active:cursor-grabbing"
        style={{ 
          transform: `translateX(${position.thumb}px)`,
          transition: isDragging ? 'none' : 'transform 0.2s ease-out'
        }}
      >
        <div className={`w-16 h-16 mx-4 bg-green-500 dark:bg-green-600 rounded-xl flex items-center justify-center shadow-lg transform transition-transform ${isDragging ? 'scale-95' : 'hover:scale-105'}`}>
          <ArrowRight className="w-8 h-8 text-white" />
        </div>
      </div>
    </div>
  );
}

export default ConfirmationSlider; 